import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables, Update } from "@/types/database";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";
import { excludeTestData } from "./testData";
import { insertWithWriteSource, type WriteSource } from "./writeSource";
import { indexDocumentRow, syncDocumentKnowledge } from "./knowledge";
import { storagePathFromFileUrl } from "./storageUrls";

export type Document = Tables<"documents">;
export type DocumentInsert = Insert<"documents">;
export type DocumentUpdate = Update<"documents">;

export async function listDocuments(): Promise<Document[]> {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .order("created_at", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function listDocumentsByPhase(phaseId: string): Promise<Document[]> {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("phase_id", phaseId)
    .order("created_at", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function listDocumentsByTask(taskId: string): Promise<Document[]> {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("task_id", taskId)
    .order("created_at", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function getDocument(id: string): Promise<Document> {
  const { data, error } = await supabase.from("documents").select("*").eq("id", id).single();
  throwIfError(error);
  return assertData(data, "Document not found");
}

function suggestDocumentTitle(title: string, category: string | null | undefined): string {
  const day = new Date().toISOString().slice(0, 10);
  const base = title
    .replace(/\.[^.]+$/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48);
  const cat = (category ?? "doc").slice(0, 24);
  return `${day}_${cat}_${base || "dokumentum"}.pdf`;
}

export async function createDocument(values: DocumentInsert, source: WriteSource = "ui"): Promise<Document> {
  const doc = await insertWithWriteSource<Document>(
    "documents",
    values as unknown as Record<string, unknown>,
    source
  );
  const suggested = suggestDocumentTitle(doc.title, doc.category);
  if (suggested !== doc.title) {
    void supabase.from("bot_proposals").insert({
      kind: "document_classify" as never,
      status: "pending",
      source: "ui",
      idempotency_key: `doc-classify-${doc.id}`,
      payload: {
        document_classify: {
          document_id: doc.id,
          suggested_title: suggested,
          category: doc.category ?? null
        }
      }
    } as never);
  }
  return indexDocumentRow({ ...doc, extraction_status: "pending" });
}

export async function updateDocument(id: string, values: DocumentUpdate): Promise<Document> {
  const { data, error } = await supabase
    .from("documents")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  const doc = assertData(data, "Document not found");
  const reextract = Object.prototype.hasOwnProperty.call(values, "file_url");
  return syncDocumentKnowledge(doc, reextract ? "extract" : "metadata");
}

export async function deleteDocument(id: string): Promise<void> {
  const { data: row } = await supabase
    .from("documents")
    .select("file_url, quote_id")
    .eq("id", id)
    .maybeSingle();

  // Quote-linked docs are owned by the quote row; clearing file_url lets the trigger remove the document.
  if (row?.quote_id) {
    const { error } = await supabase.from("quotes").update({ file_url: null }).eq("id", row.quote_id);
    throwIfError(error);
    return;
  }

  const { error } = await supabase.from("documents").delete().eq("id", id);
  throwIfError(error);

  const objectPath = storagePathFromFileUrl(row?.file_url ?? "");
  if (objectPath) {
    await supabase.storage.from("documents").remove([objectPath]);
  }
}

/**
 * Feltöltés a privát `documents` bucketbe.
 * Visszatérés: **relatív object path** (nem public URL) — a UI signed URL-t kér.
 */
export async function uploadDocumentToStorage(file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
  const path = `${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.${ext}`;
  return uploadPrivateDocument(file, path);
}

/** Quote attachments: `quotes/` prefix. */
export async function uploadQuoteFileToStorage(file: File): Promise<string> {
  return uploadPrefixedDocument(file, "quotes");
}

/** Expense receipts / company invoices: `expenses/` prefix. */
export async function uploadExpenseFileToStorage(file: File): Promise<string> {
  return uploadPrefixedDocument(file, "expenses");
}

/** Projekt AI chat csatolmányok: `chat/` prefix. */
export async function uploadChatAttachmentToStorage(file: File): Promise<string> {
  return uploadPrefixedDocument(file, "chat");
}

async function uploadPrefixedDocument(file: File, prefix: string): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
  const path = `${prefix}/${new Date().toISOString().slice(0, 10)}/${crypto.randomUUID()}.${ext}`;
  return uploadPrivateDocument(file, path);
}

async function uploadPrivateDocument(file: File, path: string): Promise<string> {
  const options: { cacheControl: string; upsert: false; contentType?: string } = {
    cacheControl: "3600",
    upsert: false
  };
  if (file.type) options.contentType = file.type;
  const { error } = await supabase.storage.from("documents").upload(path, file, options);
  if (error) throw new Error(error.message);
  return path;
}

export const documentsQueryOptions = () =>
  queryOptions({
    queryKey: qk.documents,
    queryFn: listDocuments
  });

export const documentsByTaskQueryOptions = (taskId: string) =>
  queryOptions({
    queryKey: [...qk.documents, { taskId }] as const,
    queryFn: () => listDocumentsByTask(taskId)
  });

export const documentsByPhaseQueryOptions = (phaseId: string) =>
  queryOptions({
    queryKey: [...qk.documents, { phaseId }] as const,
    queryFn: () => listDocumentsByPhase(phaseId)
  });

export const documentQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.document(id),
    queryFn: () => getDocument(id)
  });
